import React, { useState, useEffect } from 'react';
import { X } from 'lucide-react';

const COLORS = [
  '#6366f1', '#ec4899', '#f59e0b', '#10b981',
  '#3b82f6', '#ef4444', '#8b5cf6', '#14b8a6',
  '#f97316', '#84cc16',
];

const PERIOD_OPTIONS = [
  { value: 'monthly', label: '📅 Monthly', desc: 'Renews every month' },
  { value: 'quarterly', label: '📆 Quarterly', desc: 'Renews every 3 months' },
  { value: 'yearly', label: '🗓️ Yearly', desc: 'Renews every year' },
];

export default function SubscriptionModal({ subscription, onSave, onClose }) {
  const isEdit = !!subscription;

  const [form, setForm] = useState({
    name: '',
    expiry_date: '',
    renewal_period: 'yearly',
    notes: '',
    color: COLORS[0],
  });
  const [saving, setSaving] = useState(false);
  const [errors, setErrors] = useState({});

  useEffect(() => {
    if (subscription) {
      setForm({
        name: subscription.name || '',
        expiry_date: subscription.expiry_date || '',
        renewal_period: subscription.renewal_period || 'yearly',
        notes: subscription.notes || '',
        color: subscription.color || COLORS[0],
      });
    }
  }, [subscription]);

  const validate = () => {
    const e = {};
    if (!form.name.trim()) e.name = 'Name is required';
    if (!form.expiry_date) e.expiry_date = 'Expiry date is required';
    if (!form.renewal_period) e.renewal_period = 'Renewal period is required';
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validate()) return;
    setSaving(true);
    try {
      await onSave(form);
    } finally {
      setSaving(false);
    }
  };

  const set = (key) => (e) => setForm((f) => ({ ...f, [key]: e.target.value }));

  return (
    <div style={styles.overlay} onClick={(e) => e.target === e.currentTarget && onClose()}>
      <div style={styles.modal}>
        {/* Header */}
        <div style={styles.header}>
          <h2 style={styles.title}>
            {isEdit ? '✏️ Edit Subscription' : '➕ Add Subscription'}
          </h2>
          <button onClick={onClose} style={styles.closeBtn}>
            <X size={20} />
          </button>
        </div>

        <form onSubmit={handleSubmit} style={styles.form}>
          {/* Name */}
          <div style={styles.field}>
            <label style={styles.label}>Subscription Name *</label>
            <input
              style={{ ...styles.input, ...(errors.name ? styles.inputError : {}) }}
              placeholder="e.g. Netflix, AWS, Gym Membership"
              value={form.name}
              onChange={set('name')}
              maxLength={100}
            />
            {errors.name && <span style={styles.error}>{errors.name}</span>}
          </div>

          {/* Expiry Date */}
          <div style={styles.field}>
            <label style={styles.label}>Expiry / Next Renewal Date *</label>
            <input
              type="date"
              style={{ ...styles.input, ...(errors.expiry_date ? styles.inputError : {}) }}
              value={form.expiry_date}
              onChange={set('expiry_date')}
            />
            {errors.expiry_date && <span style={styles.error}>{errors.expiry_date}</span>}
          </div>

          {/* Renewal Period */}
          <div style={styles.field}>
            <label style={styles.label}>Renewal Period *</label>
            <div style={styles.periodGrid}>
              {PERIOD_OPTIONS.map((opt) => (
                <button
                  key={opt.value}
                  type="button"
                  onClick={() => setForm((f) => ({ ...f, renewal_period: opt.value }))}
                  style={{
                    ...styles.periodBtn,
                    ...(form.renewal_period === opt.value ? styles.periodBtnActive : {}),
                  }}
                >
                  <span style={styles.periodLabel}>{opt.label}</span>
                  <span style={styles.periodDesc}>{opt.desc}</span>
                </button>
              ))}
            </div>
          </div>

          {/* Notes */}
          <div style={styles.field}>
            <label style={styles.label}>Notes (optional)</label>
            <textarea
              style={styles.textarea}
              placeholder="e.g. Account email, cost, login details..."
              value={form.notes}
              onChange={set('notes')}
              rows={2}
              maxLength={500}
            />
          </div>

          {/* Color */}
          <div style={styles.field}>
            <label style={styles.label}>Colour Tag</label>
            <div style={styles.colorRow}>
              {COLORS.map((c) => (
                <button
                  key={c}
                  type="button"
                  onClick={() => setForm((f) => ({ ...f, color: c }))}
                  style={{
                    ...styles.colorDot,
                    background: c,
                    transform: form.color === c ? 'scale(1.3)' : 'scale(1)',
                    outline: form.color === c ? `3px solid ${c}` : 'none',
                    outlineOffset: '3px',
                  }}
                />
              ))}
            </div>
          </div>

          {/* Actions */}
          <div style={styles.actions}>
            <button type="button" onClick={onClose} style={styles.cancelBtn}>
              Cancel
            </button>
            <button
              type="submit"
              disabled={saving}
              style={{ ...styles.saveBtn, background: form.color }}
            >
              {saving ? '⏳ Saving...' : isEdit ? '💾 Save Changes' : '✅ Add Subscription'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

const styles = {
  overlay: {
    position: 'fixed', inset: 0,
    background: 'rgba(0,0,0,0.5)',
    backdropFilter: 'blur(4px)',
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    zIndex: 1000, padding: '20px',
  },
  modal: {
    background: 'white',
    borderRadius: '24px',
    width: '100%', maxWidth: '520px',
    maxHeight: '90vh', overflowY: 'auto',
    boxShadow: '0 32px 64px rgba(0,0,0,0.3)',
    animation: 'slideUp 0.2s ease',
  },
  header: {
    display: 'flex', alignItems: 'center', justifyContent: 'space-between',
    padding: '24px 28px 0',
  },
  title: { margin: 0, fontSize: '20px', fontWeight: '700', color: '#1a1a2e' },
  closeBtn: {
    background: '#f5f5f5', border: 'none', borderRadius: '50%',
    width: '36px', height: '36px',
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    cursor: 'pointer', color: '#666',
  },
  form: { padding: '20px 28px 28px' },
  field: { marginBottom: '20px' },
  label: {
    display: 'block', fontSize: '13px', fontWeight: '600',
    color: '#555', marginBottom: '8px', textTransform: 'uppercase', letterSpacing: '0.5px',
  },
  input: {
    width: '100%', padding: '12px 16px',
    border: '2px solid #e8e8e8',
    borderRadius: '12px', fontSize: '15px',
    outline: 'none', transition: 'border-color 0.2s',
    boxSizing: 'border-box', color: '#1a1a2e',
    background: '#fafafa',
  },
  inputError: { borderColor: '#ef4444' },
  error: { color: '#ef4444', fontSize: '12px', marginTop: '4px', display: 'block' },
  textarea: {
    width: '100%', padding: '12px 16px',
    border: '2px solid #e8e8e8',
    borderRadius: '12px', fontSize: '15px',
    outline: 'none', resize: 'vertical', fontFamily: 'inherit',
    boxSizing: 'border-box', color: '#1a1a2e', background: '#fafafa',
  },
  periodGrid: { display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '8px' },
  periodBtn: {
    padding: '12px 8px',
    border: '2px solid #e8e8e8',
    borderRadius: '12px',
    background: '#fafafa', cursor: 'pointer',
    display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '4px',
    transition: 'all 0.15s',
  },
  periodBtnActive: {
    borderColor: '#6366f1', background: '#f0f0ff',
  },
  periodLabel: { fontSize: '14px', fontWeight: '600', color: '#333' },
  periodDesc: { fontSize: '11px', color: '#888', textAlign: 'center' },
  colorRow: { display: 'flex', gap: '10px', flexWrap: 'wrap' },
  colorDot: {
    width: '28px', height: '28px',
    borderRadius: '50%', border: 'none',
    cursor: 'pointer', transition: 'transform 0.15s',
  },
  actions: {
    display: 'flex', gap: '12px', marginTop: '28px',
  },
  cancelBtn: {
    flex: 1, padding: '14px',
    border: '2px solid #e8e8e8',
    borderRadius: '12px', background: 'white',
    color: '#666', fontSize: '15px', fontWeight: '600',
    cursor: 'pointer',
  },
  saveBtn: {
    flex: 2, padding: '14px',
    border: 'none', borderRadius: '12px',
    color: 'white', fontSize: '15px', fontWeight: '700',
    cursor: 'pointer', transition: 'opacity 0.2s',
    boxShadow: '0 4px 12px rgba(0,0,0,0.2)',
  },
};
